// 頭貼裁切的幾何運算與影像輸出
//
// 與 React 無關的純邏輯集中在這裡，元件只負責互動與繪製。
// 座標系統說明：
//   - 預覽區（stage）為正方形容器，圓形裁切框固定置中，兩者中心重合
//   - offset（tx/ty）是「圖片中心相對預覽區中心」的位移，單位為 CSS px
//   - zoom 是相對於 baseScale 的倍率，zoom = 1 時圖片短邊剛好等於裁切框直徑

/** 輸出正方形邊長（px）。圓形只是顯示層的遮罩，實際存的是正方形，日後改版型不用重傳 */
export const AVATAR_OUTPUT_SIZE = 512;

/** 放大上限基準：至少 10 倍（團體照要能框出單一張臉），高解析原圖再往上放寬 */
export const ZOOM_BASE_LIMIT = 10;

/** 圓形裁切框佔預覽區短邊的比例 */
const CROP_RATIO = 0.74;

/** 非 PNG 一律轉 JPEG 時使用的品質 */
const JPEG_QUALITY = 0.92;

export interface CropGeometry {
  /** 原圖尺寸（已套用 EXIF 方向） */
  natW: number;
  natH: number;
  /** 預覽區尺寸（CSS px） */
  stageW: number;
  stageH: number;
  /** 圓形裁切框直徑（CSS px） */
  cropSize: number;
  /** 讓原圖短邊剛好等於 cropSize 的縮放係數 */
  baseScale: number;
  /** 放大上限 */
  maxZoom: number;
}

export interface CropOffset {
  tx: number;
  ty: number;
}

/**
 * 依原圖與預覽區尺寸計算裁切幾何。
 * baseScale 取短邊，確保 zoom = 1 時圖片必定蓋滿圓框，使用者不會一開場就看到白邊。
 */
export function computeGeometry(
  natW: number,
  natH: number,
  stageW: number,
  stageH: number,
): CropGeometry {
  const cropSize = Math.round(Math.min(stageW, stageH) * CROP_RATIO);
  const shortEdge = Math.min(natW, natH);
  // 原圖夠大時可以放得比基準更大，反正畫質還撐得住
  const lossless = shortEdge / AVATAR_OUTPUT_SIZE;
  return {
    natW,
    natH,
    stageW,
    stageH,
    cropSize,
    baseScale: cropSize / shortEdge,
    maxZoom: Math.max(ZOOM_BASE_LIMIT, lossless),
  };
}

/** 夾限位移，確保圓框永遠落在圖片範圍內 —— 使用者不可能框出白邊 */
export function clampOffset(geo: CropGeometry, zoom: number, offset: CropOffset): CropOffset {
  const scale = geo.baseScale * zoom;
  const maxX = Math.max(0, (geo.natW * scale - geo.cropSize) / 2);
  const maxY = Math.max(0, (geo.natH * scale - geo.cropSize) / 2);
  return {
    tx: Math.min(maxX, Math.max(-maxX, offset.tx)),
    ty: Math.min(maxY, Math.max(-maxY, offset.ty)),
  };
}

/** 預覽繪製矩形（CSS px，相對預覽區左上角） */
export function computeDrawRect(geo: CropGeometry, zoom: number, offset: CropOffset) {
  const scale = geo.baseScale * zoom;
  const dispW = geo.natW * scale;
  const dispH = geo.natH * scale;
  return {
    left: geo.stageW / 2 - dispW / 2 + offset.tx,
    top: geo.stageH / 2 - dispH / 2 + offset.ty,
    dispW,
    dispH,
  };
}

/** 圓框對應回原圖的來源正方形（原圖 px） */
export function computeSourceRect(geo: CropGeometry, zoom: number, offset: CropOffset) {
  const scale = geo.baseScale * zoom;
  const { dispW, dispH } = computeDrawRect(geo, zoom, offset);
  return {
    sx: ((dispW - geo.cropSize) / 2 - offset.tx) / scale,
    sy: ((dispH - geo.cropSize) / 2 - offset.ty) / scale,
    size: geo.cropSize / scale,
  };
}

/** 裁切區在原圖中的邊長；小於輸出尺寸代表已超過原圖解析度，存下來會糊 */
export function sourcePixelsAt(geo: CropGeometry, zoom: number): number {
  return geo.cropSize / (geo.baseScale * zoom);
}

// 滑桿 0–1 與 zoom 之間走對數映射：線性刻度會讓低倍率全擠在最前面，難以微調構圖
export const sliderToZoom = (t: number, maxZoom: number): number => Math.pow(maxZoom, t);
export const zoomToSlider = (zoom: number, maxZoom: number): number =>
  Math.log(zoom) / Math.log(maxZoom);

/** 讀取圖片檔。手機直拍的照片帶 EXIF 方向，交給瀏覽器套用，避免頭貼躺著 */
export async function loadImageBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // 舊瀏覽器不支援 imageOrientation 選項，退回預設行為
    return await createImageBitmap(file);
  }
}

/**
 * 依目前的縮放與位移輸出正方形圖檔。
 * 與預覽走同一套 computeSourceRect，所以所見即所得。
 */
export async function cropToFile(
  bitmap: ImageBitmap,
  geo: CropGeometry,
  zoom: number,
  offset: CropOffset,
  sourceFile: File,
): Promise<File> {
  const { sx, sy, size } = computeSourceRect(geo, zoom, offset);

  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_OUTPUT_SIZE;
  canvas.height = AVATAR_OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('無法建立繪圖環境');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, sx, sy, size, size, 0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);

  // PNG 進 PNG 出，保留去背功能產生的透明背景；其餘一律轉 JPEG 省流量
  const isPng = sourceFile.type === 'image/png';
  const mimeType = isPng ? 'image/png' : 'image/jpeg';
  const blob = await new Promise<Blob | null>(resolve => {
    canvas.toBlob(resolve, mimeType, isPng ? undefined : JPEG_QUALITY);
  });
  if (!blob) throw new Error('圖片處理失敗');

  const baseName = sourceFile.name.replace(/\.[^./\\]+$/, '') || 'profile';
  return new File([blob], `${baseName}-cropped.${isPng ? 'png' : 'jpg'}`, { type: mimeType });
}
